import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { waConvocation, waAGResults } from "@/lib/whatsapp";

const CHOICES = [
  { key: "pour", label: "ag_vote_pour", on: "bg-emerald-600 text-white", icon: "Check" },
  { key: "contre", label: "ag_vote_contre", on: "bg-red-600 text-white", icon: "X" },
  { key: "abstention", label: "ag_vote_abstention", on: "bg-amber-500 text-white", icon: "Minus" },
];

function ResultBars({ result, t }) {
  const total = (result.pour || 0) + (result.contre || 0) + (result.abstention || 0) || 1;
  const rows = [
    { label: t("ag_vote_pour"), val: result.pour || 0, color: "bg-emerald-600" },
    { label: t("ag_vote_contre"), val: result.contre || 0, color: "bg-red-600" },
    { label: t("ag_vote_abstention"), val: result.abstention || 0, color: "bg-amber-500" },
  ];
  return (
    <div className="space-y-2 mt-3">
      {rows.map((r) => (
        <div key={r.label}>
          <div className="flex justify-between text-xs mb-1">
            <span className="font-medium">{r.label}</span>
            <span className="text-muted-foreground">{r.val} ‰</span>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div className={`h-full ${r.color} rounded-full`} style={{ width: `${Math.round((r.val / total) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ResolutionCard({ item, index, status, canVote, choice, onChoose, t }) {
  const result = item.result;
  return (
    <div className="rounded-2xl bg-card border border-border p-4" data-testid={`ag-resolution-${item.id}`}>
      <div className="flex items-start gap-2">
        <span className="h-6 w-6 rounded-full bg-brand/10 text-brand dark:text-primary text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{index + 1}</span>
        <div className="min-w-0 flex-1">
          <p className="font-bold">{item.title}</p>
          {item.description && <p className="text-sm text-muted-foreground mt-0.5">{item.description}</p>}
        </div>
        {status === "cloturee" && result && (
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${result.adopted ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}`} data-testid={`ag-decision-${item.id}`}>
            {result.adopted ? t("ag_adopted") : t("ag_rejected")}
          </span>
        )}
      </div>

      {!item.requires_vote && (
        <p className="text-xs text-muted-foreground mt-2 italic">{t("ag_info_point")}</p>
      )}

      {item.requires_vote && status === "vote_ouvert" && canVote && (
        <div className="grid grid-cols-3 gap-2 mt-3">
          {CHOICES.map((c) => {
            const Ico = Icons[c.icon];
            const active = choice === c.key;
            return (
              <button key={c.key} onClick={() => onChoose(item.id, c.key)} data-testid={`ag-choice-${item.id}-${c.key}`}
                className={`h-11 rounded-xl text-sm font-bold flex items-center justify-center gap-1 transition-colors ${active ? c.on : "bg-secondary text-secondary-foreground"}`}>
                <Ico className="h-4 w-4" /> {t(c.label)}
              </button>
            );
          })}
        </div>
      )}

      {item.requires_vote && status === "cloturee" && result && <ResultBars result={result} t={t} />}
    </div>
  );
}

export default function AssemblyDetail() {
  const { id } = useParams();
  const { t, lang } = useApp();
  const { isRole } = useAuth();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/assemblies/${id}`);
      setA(data);
      setSel(data.my_votes || {});
    } catch (e) {
      toast.error("Erreur");
      navigate("/assemblies");
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (!a) return <div className="py-20 flex justify-center"><div className="h-8 w-8 rounded-full border-4 border-brand border-t-transparent animate-spin" /></div>;

  const isPres = isRole("president");
  const locale = lang === "ar" ? "ar-MA" : lang === "en" ? "en-GB" : "fr-FR";
  const whenStr = a.scheduled_at ? new Date(a.scheduled_at).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" }) : "—";
  const p = a.participation || { present: 0, total: 0, pct: 0, reached: false };
  const showParticipation = a.status === "vote_ouvert" || a.status === "cloturee";

  const act = async (path, okMsg, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try { await api.post(`/assemblies/${id}/${path}`); toast.success(okMsg); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const removeAG = async () => {
    if (!window.confirm(t("ag_confirm_delete"))) return;
    try { await api.delete(`/assemblies/${id}`); toast.success(t("ag_deleted")); navigate("/assemblies"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const submitVote = async () => {
    setBusy(true);
    try { await api.post(`/assemblies/${id}/vote`, { votes: sel }); toast.success(t("ag_voted")); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const downloadPv = async () => {
    try {
      const r = await api.get(`/assemblies/${id}/pv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Erreur"); }
  };
  const shareConvocation = () => window.open(waConvocation({
    title: a.title, kind: a.kind, when: whenStr, place: a.location,
    agenda: (a.agenda || []).map((it) => it.title), lang,
  }), "_blank");
  const shareResults = () => window.open(waAGResults({ title: a.title, lang }), "_blank");

  return (
    <div className="space-y-5" data-testid="assembly-detail-page">
      <button onClick={() => navigate("/assemblies")} data-testid="ag-back" className="flex items-center gap-1 text-sm text-muted-foreground">
        <Icons.ChevronLeft className="h-4 w-4 rtl:rotate-180" /> {t("assemblies")}
      </button>

      {/* En-tête */}
      <div className="rounded-2xl bg-brand text-white p-5 relative overflow-hidden">
        <div className="absolute inset-0 zellige-overlay" />
        <div className="relative z-10">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white/20">{t("ag_status_" + a.status)}</span>
            <span className="text-xs text-white/80">{t("ag_" + a.kind)}</span>
          </div>
          <h1 className="text-xl font-black">{a.title}</h1>
          <p className="text-sm text-white/85 mt-1 flex items-center gap-1.5"><Icons.Calendar className="h-4 w-4" /> {whenStr}</p>
          {a.location && <p className="text-sm text-white/85 flex items-center gap-1.5"><Icons.MapPin className="h-4 w-4" /> {a.location}</p>}
        </div>
      </div>

      {/* Participation / quorum */}
      {showParticipation && (
        <div className="rounded-2xl bg-card border border-border p-4" data-testid="ag-participation">
          <div className="flex items-center justify-between mb-2">
            <span className="font-bold">{t("ag_participation")}</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${p.reached ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}`}>
              {p.reached ? t("ag_quorum_reached") : t("ag_quorum_not_reached")}
            </span>
          </div>
          <div className="h-3 rounded-full bg-secondary overflow-hidden relative">
            <div className="h-full bg-brand rounded-full" style={{ width: `${Math.min(100, p.pct)}%` }} />
            <div className="absolute top-0 bottom-0 w-0.5 bg-foreground/60" style={{ left: `${Math.min(100, a.quorum_pct)}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">{p.present} / {p.total} ‰ · {p.pct}% ({t("ag_quorum_required")} {a.quorum_pct}%)</p>
        </div>
      )}

      {/* Poids de vote personnel */}
      {a.my_weight > 0 ? (
        <p className="text-sm text-muted-foreground flex items-center gap-1.5"><Icons.Scale className="h-4 w-4" /> {t("ag_my_weight")} : <b>{a.my_weight} ‰</b></p>
      ) : (
        <p className="text-sm text-muted-foreground flex items-center gap-1.5" data-testid="ag-cannot-vote"><Icons.Info className="h-4 w-4" /> {t("ag_cannot_vote")}</p>
      )}

      {/* Actions président */}
      {isPres && (
        <div className="flex flex-wrap gap-2" data-testid="ag-president-controls">
          {a.status === "brouillon" && (
            <>
              <button onClick={() => act("convene", t("ag_convened"))} disabled={busy} data-testid="ag-convene-btn" className="h-11 px-4 rounded-xl bg-brand text-white font-bold text-sm flex items-center gap-1.5"><Icons.Send className="h-4 w-4" /> {t("ag_convene")}</button>
              <button onClick={removeAG} data-testid="ag-delete-btn" className="h-11 px-4 rounded-xl bg-destructive/10 text-destructive font-bold text-sm flex items-center gap-1.5"><Icons.Trash2 className="h-4 w-4" /> {t("ag_delete")}</button>
            </>
          )}
          {a.status === "convoquee" && (
            <button onClick={() => act("open", t("ag_opened"))} disabled={busy} data-testid="ag-open-btn" className="h-11 px-4 rounded-xl bg-majorelle text-white font-bold text-sm flex items-center gap-1.5"><Icons.Vote className="h-4 w-4" /> {t("ag_open_vote")}</button>
          )}
          {a.status === "vote_ouvert" && (
            <button onClick={() => act("close", t("ag_closed"), t("ag_confirm_close"))} disabled={busy} data-testid="ag-close-btn" className="h-11 px-4 rounded-xl bg-brand text-white font-bold text-sm flex items-center gap-1.5"><Icons.CheckCheck className="h-4 w-4" /> {t("ag_close_vote")}</button>
          )}
        </div>
      )}

      {/* Partage & PV */}
      <div className="flex flex-wrap gap-2">
        {["convoquee", "vote_ouvert", "cloturee"].includes(a.status) && (
          <button onClick={shareConvocation} data-testid="ag-share-convocation" className="h-10 px-4 rounded-xl bg-[#25D366]/15 text-[#128C7E] dark:text-[#25D366] font-semibold text-sm flex items-center gap-1.5"><Icons.Share2 className="h-4 w-4" /> {t("ag_share_convocation")}</button>
        )}
        {a.status === "cloturee" && (
          <>
            <button onClick={shareResults} data-testid="ag-share-results" className="h-10 px-4 rounded-xl bg-[#25D366]/15 text-[#128C7E] dark:text-[#25D366] font-semibold text-sm flex items-center gap-1.5"><Icons.Share2 className="h-4 w-4" /> {t("ag_share_results")}</button>
            <button onClick={downloadPv} data-testid="ag-download-pv" className="h-10 px-4 rounded-xl bg-secondary text-secondary-foreground font-semibold text-sm flex items-center gap-1.5"><Icons.FileDown className="h-4 w-4" /> {t("ag_download_pv")}</button>
          </>
        )}
      </div>

      {/* Ordre du jour */}
      <div>
        <p className="font-bold mb-3 flex items-center gap-2"><Icons.ListChecks className="h-5 w-5 text-brand dark:text-primary" /> {t("ag_agenda")}</p>
        <div className="space-y-3">
          {(a.agenda || []).map((item, i) => (
            <ResolutionCard key={item.id} item={item} index={i} status={a.status} canVote={a.can_vote}
              choice={sel[item.id]} onChoose={(iid, c) => setSel((prev) => ({ ...prev, [iid]: c }))} t={t} />
          ))}
        </div>
      </div>

      {/* Enregistrer le vote */}
      {a.can_vote && (
        <button onClick={submitVote} disabled={busy} data-testid="ag-submit-vote" className="w-full h-12 rounded-xl bg-brand text-white font-bold disabled:opacity-60 flex items-center justify-center gap-2">
          <Icons.Vote className="h-5 w-5" /> {busy ? "…" : t("ag_submit_vote")}
        </button>
      )}
    </div>
  );
}
